import { z } from "zod";
import { now } from "../date-time/date-time";
import {
    isoAndUnixZodSchema,
    isoTimestampZodSchema,
} from "../date-time/iso-and-unix";
import { smsLogIdFactory, smsLogIdZodSchema } from "./sms-log-id";
import { smsProviderNameZodEnum } from "./sms-providers";

const smsLogZodObject = z.object({
    _id: smsLogIdZodSchema,
    _type: z.literal("SMS_LOG"),
    _createdAt: isoAndUnixZodSchema,
    _updatedAt: isoAndUnixZodSchema,
    /** Type of message */
    category: z.string().min(1),
    /** References to other stuff, like orderId, providerId etc, that you can use to filter out events by id(s) */
    refs: z.record(z.string()),
    /** To whom is the message sent? */
    to: z.string().min(1),
    /** From whom is the message sent? */
    from: z.string().min(1),
    /** Number of parts calculated for the sms to be sent, price depends on number of parts */
    parts: z.number().int().positive(),
    /**
     * Cost of the sms, we convert the amount to \
     * USD: value 0.06090 to 6090, so we store the value as integer where 1 is 0.0001 USD \
     * SEK: value 0.50 we store as 50000, so we store the value as integer where 1 is 0.0001 SEK
     */
    cost: z.number().nullable(),
    /** The price of the message copied from the providers original response */
    externalCost: z.string().or(z.number()).nullable(),
    /** False if the cost of the message has not been reported by the sms provider yet */
    costPending: z.boolean(),
    currency: z.enum(["SEK", "EUR", "USD"]),
    /** Id of the the message in the sms providers system */
    externalId: z.string(),
    /** Original status from at the sms provider of the message */
    externalStatus: z.string(),
    /** Our status, generalized to be the same no matter which sms provider has been used */
    status: z.enum(["pending", "sent", "delivered", "failed", "unknown"]),
    /** When was the message created at the sms provider */
    created: isoTimestampZodSchema,
    /** What account for the sms provider was used to send the message? */
    externalAccountName: z.string(),
    /** Name of the sms provider used to send the message */
    smsProviderName: smsProviderNameZodEnum,
    /** The uri where we can make a get request to fetch current message status */
    statusUrl: z.string().min(1).nullable(),
    /** Specific settings, what's available depends on sms provider */
    messageSettings: z.record(z.string(), z.unknown()).nullable(),
    /** Error message in any */
    errorCode: z.string().or(z.number()).nullish(),
    errorMessage: z.string().nullish(),
});

const smsLogFactoryParamsZodObject = smsLogZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    _updatedAt: true,
});

type SmsLogFactoryParams = z.infer<typeof smsLogFactoryParamsZodObject>;

const smsLogFactory = (params: SmsLogFactoryParams): SmsLog => {
    const timestamp = now();
    return {
        _id: smsLogIdFactory(),
        _type: "SMS_LOG",
        _createdAt: timestamp,
        _updatedAt: timestamp,
        ...params,
    };
};

type SmsLog = z.infer<typeof smsLogZodObject>;

export { smsLogFactory, smsLogFactoryParamsZodObject, smsLogZodObject };
export type { SmsLog, SmsLogFactoryParams };
